import { type FC, useEffect, useMemo, useRef } from "react";
import { Layout } from "@viagogo/core/ui";
import {
  Feature,
  RecordParticipationTypes,
  useFeatureParticipationRecorder,
  useFeatureTernary,
} from "@viagogo/feature";
import { type CategoryEventListViewModel, GridType } from "src/http/types";
import { FeatureSetEnum } from "src/shared/constants/FeatureSetEnum";
import { useAppContext } from "src/shared/context/AppContext";
import { getLocationCookieOptions, useLocationContext } from "src/shared/context/location-context";
import { FilterTypes } from "src/shared/enums/enums";
import { useScrollToggle } from "src/shared/hooks/useScrollToggle";
import { useWindowLocation } from "src/shared/hooks/useWindowLocation";
import type { Dates } from "src/types/shared/cookies";
import { CATEGORY_TYPE } from "src/website-apps/responsive/stubhub-category-grouping/components/constants";
import { useCategoryGroupingPageModelData } from "src/website-apps/responsive/stubhub-category-grouping/hooks/useCategoryGroupingPageModelData";
import { NewImmersiveHeaderWrapper } from "src/website-apps/shared/components/ancillary-pages/NewImmersiveHeaderWrapper";
import { branch } from "src/website-apps/shared/components/branch";
import { MarketplaceDisclosure } from "src/website-apps/shared/components/disclosures";
import { ErrorMessage } from "src/website-apps/shared/components/ErrorMessage";
import { withDefaultContexts } from "src/website-apps/shared/components/explore/withDefaultContexts";
import { withExploreContexts } from "src/website-apps/shared/components/explore/withExploreContexts";
import { withExploreExp } from "src/website-apps/shared/components/explore/withExploreExp";
import { LazyFooter } from "src/website-apps/shared/components/footer";
import { ENTITY_TYPES } from "src/website-apps/shared/components/grid/favorites-fs/constants";
import { FavoritesProvider } from "src/website-apps/shared/components/grid/favorites-fs/FavoritesContext";
import { HeaderConfiguration } from "src/website-apps/shared/components/header";
import { EventsGridContextAwareHeader } from "src/website-apps/shared/components/header/EventsGridContextAwareHeader";
import { MegaSearchProvider } from "src/website-apps/shared/components/instant-search-redesign-mega-search/MegaSearchProvider";
import { MegaSearchContext, useMegaSearchContext } from "src/website-apps/shared/components/mega-search";
import { PrimaryRadiusInMeters } from "src/website-apps/shared/constants/grid-radius";
import { TOP_LEVEL_CATEGORY_IDS_LIST } from "src/website-apps/shared/constants/TopLevelCategory";
import { useDateCookieOptions } from "src/website-apps/shared/context/date-context";
import { EventsGridContext } from "src/website-apps/shared/context/events-grid-context";
import { useGridsReducer } from "src/website-apps/shared/hooks/useGridsReducer";
import { useStableNow } from "src/website-apps/shared/hooks/useStableNow";
import { pageWrapperRecipe } from "src/website-apps/shared/styles/shared-styles";
import { getDateQueryParams } from "src/website-apps/shared/utils/getDateQueryParams";
import { getFilterQueryParams } from "src/website-apps/shared/utils/getFilterQueryParams";
import { getRecentlyViewedEventsFromCookies } from "src/website-apps/shared/utils/recently-viewed";
import { CategoryTopPerformersCarousel } from "./CategoryTopPerformersCarousel";
import { LayoutBodyManager } from "./LayoutBodyManager";

const resolveInitialFavoritesState = (model: CategoryEventListViewModel) => {
  const events = [...(model.trendingEvents?.items ?? [])];
  const eventFavorites = Object.fromEntries(events.map((curr) => [curr.eventId ?? -1, curr.isFavorite]));
  return { [ENTITY_TYPES.EVENT]: { ...eventFavorites } };
};

interface Props {
  model: CategoryEventListViewModel;
  dates?: Maybe<Dates>;
}

const AppControlComponent: FC<Props> = ({ dates, model }) => {
  const appContext = useAppContext();
  const { env } = appContext;
  const megaSearchContext = useMegaSearchContext();
  const cookieOptions = useMemo(() => getLocationCookieOptions(appContext), [appContext]);
  const [initialLocation] = useLocationContext(cookieOptions);
  const pageModel = useCategoryGroupingPageModelData();
  const initialFavorites = useMemo(() => resolveInitialFavoritesState(pageModel), [pageModel]);
  const dateCookieOptions = useDateCookieOptions();
  const execDateWithExpiration = useFeatureTernary(FeatureSetEnum.EventListExpireDateFilter);
  const dateWithExpiration = useMemo(
    () =>
      execDateWithExpiration({
        control: () => false,
        variant: () => true,
      }) ?? undefined,
    [execDateWithExpiration],
  );
  const categoryTypeId = model.categorySummary?.categoryTypeId;
  const execDefaultGridFilterType = useFeatureTernary(
    FeatureSetEnum.TMGroupingParkingPagesShowParkingEventsByDefault,
    () => categoryTypeId === CATEGORY_TYPE.PARKING,
  );
  const defaultGridFilterType = useMemo(
    () =>
      execDefaultGridFilterType({
        control: () => FilterTypes.EVENTS,
        variant: () => (categoryTypeId === CATEGORY_TYPE.PARKING ? FilterTypes.PARKING : FilterTypes.EVENTS),
        record: false,
      }),
    [categoryTypeId, execDefaultGridFilterType],
  );
  const { search } = useWindowLocation();
  const now = useStableNow();
  const datesQueryParams = useMemo(
    () => getDateQueryParams(env, search, now, dateWithExpiration, dateCookieOptions),
    [env, search, now, dateWithExpiration, dateCookieOptions],
  );
  const filters = useMemo(
    () =>
      getFilterQueryParams(search, {
        gridFilterType: defaultGridFilterType,
      }),
    [search, defaultGridFilterType],
  );

  const [mobileMenuOpen, handleOnMobileMenuToggle] = useScrollToggle();

  const participationRecorder = useFeatureParticipationRecorder(FeatureSetEnum.TMGlobalTrendingOnAncillaryPages);
  useEffect(() => {
    participationRecorder();
  }, [participationRecorder]);

  const immersiveHeaderOnAncillary = useFeatureTernary(FeatureSetEnum.TMGlobalImmersiveHeaderOnAncillaryPages);

  const showImmersiveHeader = useMemo(
    () =>
      immersiveHeaderOnAncillary({
        control: () => false,
        variant: () => true,
        record: false,
      }),
    [immersiveHeaderOnAncillary],
  );

  const recentlyViewedEventsArray = useMemo(() => getRecentlyViewedEventsFromCookies(env), [env]);

  const reducerOptions = useMemo(
    () => ({
      primaryGrid: model.eventGrids[GridType.PrimaryRadius],
      primaryFromRadius: 0,
      primaryToRadius: PrimaryRadiusInMeters,
      restGrid: model.eventGrids[GridType.Rest],
      restFromRadius: PrimaryRadiusInMeters,
      restToRadius: null,
      method: "GetFilteredEvents",
      categoryId: model.categoryId,
      location: initialLocation,
      dates: datesQueryParams,
      sortBy: filters.sortBy,
      gridFilterType: filters.gridFilterType,
      eventViewType: filters.eventViewType,
      calendarEvents: model.calendarEvents,
      recentlyViewedEvents: recentlyViewedEventsArray,
    }),
    [
      datesQueryParams,
      filters.eventViewType,
      filters.gridFilterType,
      filters.sortBy,
      initialLocation,
      model.calendarEvents,
      model.categoryId,
      model.eventGrids,
      recentlyViewedEventsArray,
    ],
  );
  const gridsContext = useGridsReducer(reducerOptions);
  const [gridsState] = gridsContext;

  // SSR ships empty grids — always fetch from LocationSearch on mount.
  const initialGridsContext = useRef(gridsContext);
  useEffect(() => {
    const [state, dispatch, methods] = initialGridsContext.current;
    methods.setDates(dispatch, state, state.filters.dates ?? undefined);
  }, []);

  return (
    <div className={pageWrapperRecipe({ mobileMenu: mobileMenuOpen ? "open" : "closed" })}>
      <EventsGridContext.Provider value={gridsContext}>
        <FavoritesProvider data={initialFavorites}>
          <Layout
            header={
              <MegaSearchProvider>
                <NewImmersiveHeaderWrapper
                  tlcId={
                    !model.isImmersivePage &&
                    model.categoryId &&
                    !TOP_LEVEL_CATEGORY_IDS_LIST.includes(model.categoryId)
                      ? model.tlcId
                      : null
                  }
                  headerTitle={model.categorySummary?.categoryName}
                  categoryId={model.categoryId}
                  dates={dates}
                  location={gridsState.filters.location}
                  leagueContent={model.leagueHeaderContent}
                  logoUrl={model.leagueLogoImageUrl}
                  hideTrending
                  carouselSlot={
                    !model.isImmersivePage && model.popularCategories ? (
                      <CategoryTopPerformersCarousel
                        categoryId={model.categoryId}
                        categoryName={model.categorySummary?.categoryName ?? undefined}
                        popularCategories={model.popularCategories}
                      />
                    ) : undefined
                  }
                >
                  <ErrorMessage />
                  {!appContext.isNativeApp && !megaSearchContext.active && (
                    <MarketplaceDisclosure marketplaceDisclosureSiteMessage={model.header.marketplaceDisclosure} />
                  )}
                  <Feature
                    id={FeatureSetEnum.TMGlobalImmersiveHeaderOnAncillaryPages}
                    recordType={RecordParticipationTypes.MANUAL}
                    control={() => (
                      <MegaSearchContext.Provider value={megaSearchContext}>
                        <EventsGridContextAwareHeader
                          browseHeader
                          discoveryLinks={model.header.navigationBarLinks}
                          isUserLoggedIn={appContext.isUserLoggedIn}
                          onMobileMenuToggle={handleOnMobileMenuToggle}
                          mobileMenuOpen={mobileMenuOpen}
                        />
                      </MegaSearchContext.Provider>
                    )}
                    variant={() => (
                      <MegaSearchContext.Provider value={megaSearchContext}>
                        <HeaderConfiguration.Browse
                          discoveryLinks={model.header.navigationBarLinks}
                          isUserLoggedIn={appContext.isUserLoggedIn}
                          onMobileMenuToggle={handleOnMobileMenuToggle}
                          bigPerformerBanner={!!showImmersiveHeader}
                          mobileMenuOpen={mobileMenuOpen}
                        />
                      </MegaSearchContext.Provider>
                    )}
                  />
                </NewImmersiveHeaderWrapper>
              </MegaSearchProvider>
            }
            body={<LayoutBodyManager model={model} />}
            footer={
              <LazyFooter
                companyLinks={model.footer.companyLinks}
                supportLinks={model.footer.supportLinks}
                getLocationSettingsUrl={model.footer.getLocationSettingsUrl}
              />
            }
          />
        </FavoritesProvider>
      </EventsGridContext.Provider>
    </div>
  );
};

export const AppControlComponentWithContexts = branch(
  (props: Props & { exploreEnabled?: Maybe<boolean> }) => !!props.exploreEnabled,
  withExploreContexts,
  withDefaultContexts,
)(AppControlComponent);
export const AppControl = withExploreExp(AppControlComponentWithContexts);
